import { useCallback, useEffect, useState } from 'react';

import { PermissionsTable } from './PermissionsTable';
import {
  addUserToData,
  generateInheritedTableData,
  generateTableData,
  generateUpdateData,
  getGrantsScope,
  isValidScope,
} from './permissionUtils';
import { TableDataShape } from './Table';

export type PermissionsTableContainerProps = {
  menuLinkGrants: MenuLinkGrantsDto;
  onUpdate: (update: UpdateMenuLinkGrantsDto) => void;
  isLoading: boolean;
  isUpdating: boolean;
  isProcessing: boolean;
  nodeName: string;
  updateSuccess?: boolean;
};

export const PermissionsTableContainer = ({
  menuLinkGrants,
  onUpdate,
  isLoading,
  isUpdating,
  isProcessing,
  nodeName,
  updateSuccess,
}: PermissionsTableContainerProps) => {
  const [scope, setScope] = useState<MenuLinkGrantScope>('inherit');
  const [originalScope, setOriginalScope] = useState(scope);
  const [dataInherited, setDataInherited] = useState<TableDataShape>([]);
  const [data, setData] = useState<TableDataShape>([]);
  const [originalData, setOriginalData] = useState(data);
  const [hasChanges, setHasChanges] = useState(false);

  // Trigger: (re)generate table data when grants data updates
  useEffect(() => {
    if (menuLinkGrants) {
      const dataInherited = generateInheritedTableData(menuLinkGrants);
      setDataInherited(dataInherited);
      const data = generateTableData(menuLinkGrants);
      setData(data);
      setOriginalData(data);
      setHasChanges(false);
      const scope = getGrantsScope(menuLinkGrants);
      setScope(scope);
      setOriginalScope(scope);
    }
  }, [menuLinkGrants]);

  // Handler: update changes
  const handleUpdate = useCallback((rowIndex: number, columnId: string, value: boolean) => {
    setData(old =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnId]: value,
          };
        }
        return row;
      }),
    );
    setHasChanges(true);
  }, []);

  // Handler: reset changes
  const handleReset = useCallback(() => {
    setData(originalData);
    setScope(originalScope);
    setHasChanges(false);
  }, [originalData, originalScope]);

  // Handler: save changes
  const handleSave = useCallback(() => {
    if (menuLinkGrants?.groups && hasChanges) {
      onUpdate(generateUpdateData(menuLinkGrants.groups, data, scope));
    }
  }, [menuLinkGrants, hasChanges, onUpdate, data, scope]);

  // Handler: changing scope
  const handleChangeScope = useCallback(
    (nextScope: string) => {
      if (isValidScope(nextScope)) {
        // When changing to a different scope than 'inherit', take inherit grants to new scope
        if (originalScope === 'inherit' && scope === 'inherit' && nextScope !== 'inherit') {
          setData(dataInherited);
        }
        setScope(nextScope);
        setHasChanges(true);
      }
    },
    [dataInherited, originalScope, scope],
  );

  // Handler: add user
  const handleAddUser = useCallback(
    (name: string, id: number) => {
      if (name && scope !== 'inherit') {
        const rowsBeforeAdd = data.length;
        const newData = addUserToData(data, name, id);
        const rowsAfterAdd = newData.length;
        if (rowsBeforeAdd !== rowsAfterAdd) {
          setData(newData);
          setHasChanges(true);
        }
      }
    },
    [scope, data],
  );

  return (
    <PermissionsTable
      isLoading={isLoading}
      isProcessing={isProcessing}
      isUpdating={isUpdating}
      hasChanges={hasChanges}
      data={scope === 'inherit' ? dataInherited : data}
      onReset={handleReset}
      onSave={handleSave}
      onUpdate={handleUpdate}
      nodeName={nodeName}
      onChangeScope={handleChangeScope}
      onAddUser={handleAddUser}
      scope={scope}
      updateSuccess={updateSuccess}
    />
  );
};
